import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { calculateDateTimeDiffMinutes, formatMinutesAsHHMM, getFlightMinGroundTimeMinutes, normalizeMinutes } from "../flightTimingUtils";
import { normalizeAircraftIdentifier, normalizeAirportCode } from "../routeUtils";
import { getAirportConstraint, projectAirportCurfewWindows } from "../validation/curfewRuleUtils";
import { getScheduleDraftTransportDemands } from "../../constants/transportDemandDefaults";

dayjs.extend(utc);

const FERRY_CLASS_NAME = "schedule-draft-timeline-item-flight-ferry";
const GROUND_LABEL_HORIZONTAL_MIN_WIDTH = 64;
const GROUND_LABEL_HORIZONTAL_CHAR_WIDTH = 7;
const GROUND_LABEL_HORIZONTAL_PADDING = 20;
const GROUND_LABEL_VERTICAL_MIN_WIDTH = 28;
const GROUND_MARKER_COMPACT_MIN_WIDTH = 12;
const SELECTED_FLIGHT_CLASS_NAME = "schedule-draft-timeline-item-selected";
const SELECTED_GROUND_CLASS_NAME = "schedule-draft-timeline-ground-selected";
const WEAK_SELECTED_GROUND_CLASS_NAME = "schedule-draft-timeline-ground-weak-selected";
const GROUND_TIME_NEUTRAL_THRESHOLD_MINUTES = 1;

function toArray(value) {
    return Array.isArray(value) ? value : [];
}

function getSortableTime(value) {
    const timestamp = value ? new Date(value).getTime() : NaN;
    return Number.isFinite(timestamp) ? timestamp : Number.MAX_SAFE_INTEGER;
}

function getFlightId(flight) {
    const flightId = flight?.id ?? flight?.flight_id ?? flight?.flightId;
    return flightId === undefined || flightId === null || flightId === "" ? null : String(flightId);
}

function getFlightRotationId(flight) {
    const rotationId = flight?.rotation_no ?? flight?.rotationNo ?? flight?.rotation_id ?? flight?.rotationId;
    return rotationId === undefined || rotationId === null || rotationId === "" ? null : String(rotationId);
}

function getFlightAircraftKey(flight) {
    return normalizeAircraftIdentifier(flight?.aircraft_reg ?? flight?.aircraftReg ?? "UNASSIGNED") || "UNASSIGNED";
}

function getFlightDisplayOrder(flight) {
    const displayOrder = Number(flight?.display_order ?? flight?.displayOrder);
    return Number.isFinite(displayOrder) ? displayOrder : Number.MAX_SAFE_INTEGER;
}

function buildGroundAdjacentFlightSummary(flight) {
    if (!flight) {
        return null;
    }

    return {
        id: getFlightId(flight),
        flightNumber: flight.flight_number || flight.flightNumber || "未命名航班",
        aircraftReg: flight.aircraft_reg || flight.aircraftReg || null,
        departureAirport: normalizeAirportCode(flight.departure_airport || flight.departureAirport),
        arrivalAirport: normalizeAirportCode(flight.arrival_airport || flight.arrivalAirport),
        std: flight.std || null,
        sta: flight.sta || null,
    };
}

function isFlightTimelineItem(item) {
    return Boolean(item?.meta?.flight && item?.itemType !== "ground" && item?.meta?.itemType !== "ground");
}

function isGroundTimelineItem(item) {
    return item?.itemType === "ground" || item?.meta?.itemType === "ground";
}

function appendClassNames(item, classNames = []) {
    return [item?.className, ...classNames].filter(Boolean).join(" ");
}

function getGroundTimeComparison(groundTimeMinutes, minGroundTimeMinutes) {
    const actualMinutes = normalizeMinutes(groundTimeMinutes);
    const minimumMinutes = normalizeMinutes(minGroundTimeMinutes);
    if (actualMinutes === null || minimumMinutes === null || minimumMinutes <= 0) {
        return null;
    }

    const diffMinutes = actualMinutes - minimumMinutes;
    const status = Math.abs(diffMinutes) <= GROUND_TIME_NEUTRAL_THRESHOLD_MINUTES
        ? "neutral"
        : diffMinutes > 0
            ? "surplus"
            : "shortage";
    const baseRatio = status === "surplus"
        ? Math.max(0, Math.min(100, (minimumMinutes / actualMinutes) * 100))
        : 100;
    const surplusRatio = status === "surplus" ? Math.max(0, 100 - baseRatio) : 0;
    const shortageRatio = status === "shortage"
        ? Math.max(0, Math.min(100, ((minimumMinutes - actualMinutes) / minimumMinutes) * 100))
        : 0;

    return {
        status,
        minGroundTimeMinutes: minimumMinutes,
        diffMinutes,
        baseRatio,
        surplusRatio,
        shortageRatio,
    };
}

function formatGroundRatio(value) {
    const ratio = Number(value);
    return Number.isFinite(ratio) ? Math.max(0, Math.min(100, ratio)).toFixed(2) : null;
}

function buildGroundTimeStyle(comparison) {
    if (!comparison) {
        return null;
    }

    return [
        `--schedule-draft-ground-min-ratio: ${formatGroundRatio(comparison.baseRatio)}%`,
        `--schedule-draft-ground-surplus-ratio: ${formatGroundRatio(comparison.surplusRatio)}%`,
        `--schedule-draft-ground-shortage-ratio: ${formatGroundRatio(comparison.shortageRatio)}%`,
    ].filter(Boolean).join("; ");
}

function isSelectedTimelineItem(item, selectedItemId) {
    return Boolean(selectedItemId && String(item?.id || "") === String(selectedItemId));
}

function getGroundAirportCode(item) {
    if (!isGroundTimelineItem(item) || item?.selectable === false) {
        return "";
    }

    return normalizeAirportCode(item?.meta?.ground?.airportCode);
}

function getSelectedGroundAirportCode(items, selectedItemId) {
    const selectedGroundItem = toArray(items).find((item) => isSelectedTimelineItem(item, selectedItemId) && isGroundTimelineItem(item) && item?.selectable !== false);
    return getGroundAirportCode(selectedGroundItem);
}

function getCurfewOperationDisplay(operations = []) {
    const normalizedOperations = Array.from(new Set(operations.filter(Boolean)));
    const hasDeparture = normalizedOperations.includes("departure");
    const hasArrival = normalizedOperations.includes("arrival");

    if (hasDeparture && hasArrival) {
        return {
            operation: "mixed",
            displayLabel: "起/降宵禁",
            compactLabel: "起/降宵禁",
            badgeLabel: "起/降",
            tone: "mixed",
        };
    }

    if (hasArrival) {
        return {
            operation: "arrival",
            displayLabel: "降落宵禁",
            compactLabel: "降宵禁",
            badgeLabel: "降",
            tone: "arrival",
        };
    }

    return {
        operation: "departure",
        displayLabel: "起飞宵禁",
        compactLabel: "起宵禁",
        badgeLabel: "起",
        tone: "departure",
    };
}

function sortTimelineFlightItems(left, right) {
    const timeDelta = getSortableTime(left?.start) - getSortableTime(right?.start);
    if (timeDelta) {
        return timeDelta;
    }

    const orderDelta = getFlightDisplayOrder(left?.meta?.flight) - getFlightDisplayOrder(right?.meta?.flight);
    if (orderDelta) {
        return orderDelta;
    }

    return String(left?.id || "").localeCompare(String(right?.id || ""));
}

function getRotationRole(index, total) {
    if (total <= 1) {
        return "single";
    }
    if (index === 0) {
        return "start";
    }
    if (index === total - 1) {
        return "end";
    }
    return "middle";
}

function getRotationRoleByItemId(items, demandDestinationAirports = new Set()) {
    const groups = new Map();

    items.filter(isFlightTimelineItem).forEach((item) => {
        const flight = item.meta.flight;
        const rotationId = getFlightRotationId(flight);
        if (!rotationId) {
            return;
        }

        const groupKey = [item.trackId, getFlightAircraftKey(flight), rotationId].join("::");
        const groupItems = groups.get(groupKey) || [];
        groupItems.push(item);
        groups.set(groupKey, groupItems);
    });

    const roleByItemId = new Map();
    groups.forEach((groupItems) => {
        const sortedItems = [...groupItems].sort(sortTimelineFlightItems);
        sortedItems.forEach((item, index) => {
            const flight = item.meta.flight;
            const arrivalAirport = normalizeAirportCode(flight?.arrival_airport);
            roleByItemId.set(item.id, {
                rotationId: getFlightRotationId(flight),
                role: getRotationRole(index, sortedItems.length),
                endsAtTransportDemandDestination: Boolean(arrivalAirport && demandDestinationAirports.has(arrivalAirport)),
            });
        });
    });

    return roleByItemId;
}

function isFerryTimelineFlight(flight) {
    if (flight?.is_ferry || flight?.isFerry || flight?.ferry === true) {
        return true;
    }

    const text = [
        flight?.stc_type,
        flight?.service_type,
        flight?.flight_type,
        flight?.operation_type,
        flight?.remarks,
    ].filter(Boolean).join(" ").toUpperCase();

    return /FERRY|POSITION|REPOSITION|\bPOS\b|调机|回程/.test(text);
}

function decorateTimelineFlightItems(items, { demandDestinationAirports = new Set() } = {}) {
    const roleByItemId = getRotationRoleByItemId(items, demandDestinationAirports);

    return items.map((item) => {
        if (!isFlightTimelineItem(item)) {
            return item;
        }

        const rotation = roleByItemId.get(item.id) || null;
        const isFerry = isFerryTimelineFlight(item.meta.flight);
        const classNames = [isFerry ? FERRY_CLASS_NAME : null];

        return {
            ...item,
            itemType: "flight",
            className: appendClassNames(item, classNames),
            meta: {
                ...(item.meta || {}),
                itemType: "flight",
                timelineVisual: {
                    ...(item.meta?.timelineVisual || {}),
                    rotation: rotation
                        ? {
                              id: rotation.rotationId,
                              role: rotation.role,
                              source: "flight.rotation_no",
                              endsAtTransportDemandDestination: rotation.endsAtTransportDemandDestination,
                          }
                        : null,
                    isFerry,
                },
            },
        };
    });
}

function estimateGroundMarkerDisplayMode(groundTimeMinutes) {
    const minutes = Number(groundTimeMinutes);
    if (!Number.isFinite(minutes) || minutes <= 0) {
        return "tooltip";
    }
    if (minutes >= 120) {
        return "horizontal";
    }
    if (minutes >= 45) {
        return "vertical";
    }
    return "compact";
}

export function resolveGroundMarkerDisplayMode(widthPx, options = {}) {
    const width = Number(widthPx);
    const displayOptions = typeof options === "string" ? { displayLabel: options } : options;
    const displayLabel = String(displayOptions?.displayLabel || "HKG");
    const markerRole = displayOptions?.markerRole || "turnaround";
    const horizontalWidth = Math.max(
        GROUND_LABEL_HORIZONTAL_MIN_WIDTH,
        displayLabel.length * GROUND_LABEL_HORIZONTAL_CHAR_WIDTH + GROUND_LABEL_HORIZONTAL_PADDING,
    );
    const canUseVerticalLabel = markerRole !== "discontinuity" && !/[→-]/.test(displayLabel);

    if (!Number.isFinite(width) || width < GROUND_MARKER_COMPACT_MIN_WIDTH) {
        return "tooltip";
    }
    if (width >= horizontalWidth) {
        return "horizontal";
    }
    if (canUseVerticalLabel && width >= GROUND_LABEL_VERTICAL_MIN_WIDTH) {
        return "vertical";
    }
    return "compact";
}

export function formatTimelineClockTime(value) {
    if (!value) {
        return "--:--";
    }

    const parsed = dayjs.utc(value);
    if (parsed.isValid()) {
        return parsed.format("HH:mm");
    }

    const text = String(value).trim();
    const clockMatch = text.match(/^(\d{1,2}):(\d{2})/) || text.match(/[T\s](\d{2}):(\d{2})/);
    return clockMatch ? `${clockMatch[1].padStart(2, "0")}:${clockMatch[2]}` : text || "--:--";
}

export function formatGroundTimeDuration(minutes) {
    return formatMinutesAsHHMM(minutes);
}

export function getTransportDemandDestinationAirports(draft) {
    return new Set(
        getScheduleDraftTransportDemands(draft)
            .map((demand) => normalizeAirportCode(demand?.arrival_airport || demand?.destination || demand?.destination_airport || demand?.arr))
            .filter(Boolean),
    );
}

export function buildGroundTimelineItems(items, { groundSelectable = true, airportConstraintsMap = {} } = {}) {
    const groups = new Map();

    items.filter(isFlightTimelineItem).forEach((item) => {
        const aircraftKey = normalizeAircraftIdentifier(item.meta.flight?.aircraft_reg ?? item.meta.flight?.aircraftReg);
        if (!aircraftKey) {
            return;
        }

        const groupKey = [item.trackId, aircraftKey].join("::");
        const groupItems = groups.get(groupKey) || [];
        groupItems.push(item);
        groups.set(groupKey, groupItems);
    });

    const groundItems = [];
    groups.forEach((groupItems) => {
        const sortedItems = [...groupItems].sort(sortTimelineFlightItems);
        for (let index = 0; index < sortedItems.length - 1; index += 1) {
            const previousItem = sortedItems[index];
            const nextItem = sortedItems[index + 1];
            const groundTimeMinutes = calculateDateTimeDiffMinutes(previousItem.end, nextItem.start);
            if (groundTimeMinutes === null || groundTimeMinutes <= 0) {
                continue;
            }

            const previousFlight = previousItem.meta.flight;
            const nextFlight = nextItem.meta.flight;
            const arrivalAirport = normalizeAirportCode(previousFlight?.arrival_airport);
            const departureAirport = normalizeAirportCode(nextFlight?.departure_airport);
            const isContinuous = Boolean(arrivalAirport && departureAirport && arrivalAirport === departureAirport);
            const airportCode = isContinuous ? arrivalAirport : null;
            const groundTimeLabel = formatGroundTimeDuration(groundTimeMinutes);
            const minGroundTimeMinutes = isContinuous ? getFlightMinGroundTimeMinutes(previousFlight, airportConstraintsMap) : null;
            const groundTimeComparison = getGroundTimeComparison(groundTimeMinutes, minGroundTimeMinutes);
            const displayMode = estimateGroundMarkerDisplayMode(groundTimeMinutes);
            const displayLabel = isContinuous ? airportCode : `${arrivalAirport || "--"}->${departureAirport || "--"}`;
            const compactLabel = isContinuous ? "" : "!";
            const markerRole = isContinuous ? "turnaround" : "discontinuity";
            const continuityLabel = isContinuous ? airportCode : displayLabel;
            const tooltip = isContinuous
                ? `${airportCode} ground time ${groundTimeLabel}`
                : `Airport discontinuity ${continuityLabel}; gap ${groundTimeLabel}`;

            groundItems.push({
                id: `ground:${previousItem.id}:${nextItem.id}`,
                trackId: previousItem.trackId,
                itemType: "ground",
                selectable: groundSelectable,
                title: isContinuous ? airportCode : "不连续",
                subtitle: `GT ${groundTimeLabel}`,
                start: previousItem.end,
                end: nextItem.start,
                variant: "ground",
                className: [
                    isContinuous ? "schedule-draft-timeline-ground-continuous" : "schedule-draft-timeline-ground-discontinuous",
                    `schedule-draft-timeline-ground-mode-${displayMode}`,
                    groundTimeComparison ? `schedule-draft-timeline-ground-time-${groundTimeComparison.status}` : null,
                ].filter(Boolean).join(" "),
                style: buildGroundTimeStyle(groundTimeComparison),
                meta: {
                    itemType: "ground",
                    trackKind: "ground",
                    ground: {
                        previousItemId: previousItem.id,
                        nextItemId: nextItem.id,
                        previousFlightId: getFlightId(previousFlight),
                        nextFlightId: getFlightId(nextFlight),
                        aircraftReg: previousFlight?.aircraft_reg ?? previousFlight?.aircraftReg ?? null,
                        airportCode,
                        displayLabel,
                        compactLabel,
                        arrivalAirport: arrivalAirport || null,
                        departureAirport: departureAirport || null,
                        isContinuous,
                        markerRole,
                        groundTimeMinutes,
                        groundTimeLabel,
                        minGroundTimeMinutes: groundTimeComparison?.minGroundTimeMinutes ?? null,
                        minGroundTimeLabel: groundTimeComparison ? formatGroundTimeDuration(groundTimeComparison.minGroundTimeMinutes) : null,
                        groundTimeDiffMinutes: groundTimeComparison?.diffMinutes ?? null,
                        groundTimeStatus: groundTimeComparison?.status ?? null,
                        groundTimeBaseRatio: groundTimeComparison?.baseRatio ?? null,
                        groundTimeSurplusRatio: groundTimeComparison?.surplusRatio ?? null,
                        groundTimeShortageRatio: groundTimeComparison?.shortageRatio ?? null,
                        displayMode,
                        tooltip,
                        previousFlight: buildGroundAdjacentFlightSummary(previousFlight),
                        nextFlight: buildGroundAdjacentFlightSummary(nextFlight),
                    },
                },
            });
        }
    });

    return groundItems;
}

export function applyTimelineSelection(items, selectedItemId) {
    if (!selectedItemId) {
        return items;
    }

    const selectedAirportCode = getSelectedGroundAirportCode(items, selectedItemId);

    return toArray(items).map((item) => {
        const isSelectedItem = isSelectedTimelineItem(item, selectedItemId);
        if (isSelectedItem) {
            if (isGroundTimelineItem(item) && item?.selectable === false) {
                return item;
            }

            const selectedClassName = isGroundTimelineItem(item) ? SELECTED_GROUND_CLASS_NAME : SELECTED_FLIGHT_CLASS_NAME;
            return {
                ...item,
                selected: true,
                selectionStrength: "strong",
                className: appendClassNames(item, [selectedClassName]),
                meta: {
                    ...(item.meta || {}),
                    selected: true,
                    selectionStrength: "strong",
                },
            };
        }

        const airportCode = getGroundAirportCode(item);
        if (!selectedAirportCode || !airportCode || airportCode !== selectedAirportCode) {
            return item;
        }

        return {
            ...item,
            relatedSelected: true,
            selectionStrength: "weak",
            className: appendClassNames(item, [WEAK_SELECTED_GROUND_CLASS_NAME]),
            meta: {
                ...(item.meta || {}),
                relatedSelected: true,
                selectionStrength: "weak",
            },
        };
    });
}

export function buildSelectedAirportCurfewTimelineItems({ items, selectedItemId, airportConstraintsMap = {}, timeWindow } = {}) {
    if (!selectedItemId || !timeWindow?.start || !timeWindow?.end) {
        return [];
    }

    const selectedGroundItem = toArray(items).find((item) => isSelectedTimelineItem(item, selectedItemId) && isGroundTimelineItem(item));
    const ground = selectedGroundItem?.meta?.ground || null;
    const airportCode = normalizeAirportCode(ground?.airportCode);
    if (!selectedGroundItem || !airportCode) {
        return [];
    }

    const constraint = getAirportConstraint(airportConstraintsMap, airportCode);
    const windows = projectAirportCurfewWindows({
        airportCode,
        constraint,
        operations: ["arrival", "departure"],
        aircraftReg: ground?.aircraftReg,
        timeWindow,
    });

    return windows.map((window, index) => {
        const display = getCurfewOperationDisplay(window.operations || [window.operation]);
        return {
            id: `curfew:${selectedGroundItem.id}:${index}:${display.operation}:${window.start}`,
            trackId: selectedGroundItem.trackId,
            itemType: "curfew",
            type: "background",
            selectable: false,
            title: `${airportCode} ${display.displayLabel} ${window.label} UTC`,
            subtitle: `${window.label} UTC`,
            start: window.start,
            end: window.end,
            variant: "curfew",
            className: `schedule-draft-timeline-curfew-background schedule-draft-timeline-curfew-background-${display.tone}`,
            meta: {
                itemType: "curfew",
                trackKind: "curfew",
                curfew: {
                    ...window,
                    airportCode,
                    operation: display.operation,
                    displayLabel: display.displayLabel,
                    compactLabel: display.compactLabel,
                    badgeLabel: display.badgeLabel,
                    tone: display.tone,
                    edgeStartLabel: window.windowStartLabel,
                    edgeEndLabel: window.windowEndLabel,
                    sourceGroundItemId: selectedGroundItem.id,
                    aircraftReg: ground?.aircraftReg || null,
                },
            },
        };
    });
}

export function buildTimelineVisualItems(items, options = {}) {
    const flightItems = decorateTimelineFlightItems(toArray(items), options);
    return [...flightItems, ...buildGroundTimelineItems(flightItems, options)];
}
